import { Form, Alert } from 'antd';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { ControlledFormItem, Button } from '@/shared/components/ui/atoms';
import successImage from '@/assets/images/success.png';
import type { ResetPasswordFormData, ResetPasswordFormProps } from './types';
import './ResetPasswordForm.css';

/**
 * Special characters accepted by the password policy: @ $ ! % * ? & - + = _ # ;
 * Must stay in sync with the API's IsStrongPassword decorator
 * (auth-bridge-api, src/shared/decorators/strong-password.decorator.ts).
 */
const PASSWORD_SPECIAL_CHARS = /[@$!%*?&+=_#;-]/;

/**
 * Inline SVG icon for failed password rules ($icon/close_small from Figma).
 * Uses currentColor to inherit the parent's text color.
 */
const CloseSmallIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    role="img"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
    />
  </svg>
);

/**
 * Inline SVG icon for passed password rules ($icon/check_small from Figma).
 * Uses currentColor to inherit the parent's text color.
 */
const CheckSmallIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    role="img"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
    />
  </svg>
);

/**
 * ResetPasswordForm Organism
 *
 * A form component to reset password with secure validation.
 * Validates password strength with multiple security requirements.
 *
 * ## Features
 * - **Password Validation**: 12-64 characters with complexity requirements
 * - **Real-time Feedback**: Visual indicators for password requirements
 * - **Secure Patterns**: Prevents numeric sequences and weak passwords
 * - **Password Confirmation**: Validates password match
 * - **Loading State**: Disables form during submission
 * - **Error/Success Feedback**: Displays API responses
 *
 * @example
 * Basic usage
 * ```tsx
 * <ResetPasswordForm
 *   onSubmit={handleResetPassword}
 *   loading={isLoading}
 *   error={errorMessage}
 *   success={passwordReset}
 * />
 * ```
 *
 * @param props - ResetPasswordForm component props
 * @returns A complete reset password form with validation
 */
const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  onSubmit,
  loading = false,
  error = null,
  success = false,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const resetPasswordSchema = useMemo(
    () =>
      z
        .object({
          newPassword: z
            .string()
            .min(12, t('Password must be between 12 and 64 characters'))
            .max(64, t('Password must be between 12 and 64 characters'))
            .regex(/[A-Z]/, t('Password must contain uppercase letters'))
            .regex(/[a-z]/, t('Password must contain lowercase letters'))
            .regex(/\d/, t('Password must contain numbers'))
            .regex(
              PASSWORD_SPECIAL_CHARS,
              t('Password must contain special characters')
            )
            .refine(
              (val) => !/012|123|234|345|456|567|678|789|890/.test(val),
              t('Password must not contain numeric sequences')
            ),
          confirmPassword: z.string().min(1, t('Please confirm your password')),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          message: t('Passwords do not match'),
          path: ['confirmPassword'],
        }),
    [t]
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const [password, confirmPassword] = useWatch({
    control,
    name: ['newPassword', 'confirmPassword'],
  });

  const passwordValidation = useMemo(() => {
    const isMinLength = password ? password.length >= 12 : false;

    return {
      hasMinLength: password
        ? password.length >= 12 && password.length <= 64
        : false,
      hasUpperCase: isMinLength ? /[A-Z]/.test(password) : false,
      hasLowerCase: isMinLength ? /[a-z]/.test(password) : false,
      hasNumber: isMinLength ? /\d/.test(password) : false,
      hasSpecialChar: isMinLength
        ? PASSWORD_SPECIAL_CHARS.test(password)
        : false,
      noSequence: isMinLength
        ? !/012|123|234|345|456|567|678|789|890/.test(password)
        : false,
    };
  }, [password]);

  /** Whether every password strength rule is satisfied */
  const allRulesValid = useMemo(
    () =>
      passwordValidation.noSequence &&
      passwordValidation.hasUpperCase &&
      passwordValidation.hasLowerCase &&
      passwordValidation.hasNumber &&
      passwordValidation.hasSpecialChar &&
      passwordValidation.hasMinLength,
    [passwordValidation]
  );

  const handleFormSubmit = async (data: ResetPasswordFormData) => {
    const { newPassword } = data;
    await onSubmit({ newPassword });
  };

  const isMismatch =
    errors.confirmPassword?.message === t('Passwords do not match');

  if (success) {
    return (
      <div className="auth-page-container">
        <div className="auth-container">
          <div className="reset-password-success-wrapper">
            <img
              src={successImage}
              alt="Success"
              className="reset-password-success-image"
            />
            <div className="reset-password-success-content">
              <h1 className="reset-password-success-title">
                {t('Password registered!')}
              </h1>
              <p className="reset-password-success-subtitle">
                {t('Log in by clicking the button below.')}
              </p>
            </div>
            <Button
              variant="primary"
              size="lg"
              htmlType="button"
              onClick={() => navigate('/login')}
            >
              {t('Sign in')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container">
      <div className="auth-container">
        <div className="reset-password-form-wrapper">
          <div className="reset-password-form-header">
            <div className="reset-password-form-header-content">
              <p className="reset-password-form-header-small">
                {t('Identity and Fraud Platform')}
              </p>
              <h1 className="reset-password-form-header-title">
                {t('Create a new password')}
              </h1>
            </div>

            <div className="reset-password-form-header-description">
              <p>{t('Choose a secure password to access your account')}</p>
            </div>
          </div>

          {(error || isMismatch) && (
            <Alert
              message={
                <span>
                  <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                    {t('Attention!')}
                  </span>{' '}
                  {error ||
                    (errors.confirmPassword
                      ? t('Typed passwords are not identical')
                      : null)}
                </span>
              }
              type="error"
              showIcon
              closable
            />
          )}

          <Form
            layout="vertical"
            onFinish={handleSubmit(handleFormSubmit)}
            noValidate
            className="reset-password-form"
          >
            <ControlledFormItem
              name="newPassword"
              control={control as never}
              label={t('Password')}
              inputType="password"
              inputProps={{
                placeholder: t('Enter your password here'),
                autoComplete: 'new-password',
              }}
            />

            <div className="password-requirements">
              <div
                className={`requirement ${passwordValidation.noSequence ? 'valid' : 'invalid'}`}
              >
                {passwordValidation.noSequence ? (
                  <CheckSmallIcon />
                ) : (
                  <CloseSmallIcon />
                )}
                <span>{t('Do not use numeric sequences')}</span>
              </div>
              <div
                className={`requirement ${passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? 'valid' : 'invalid'}`}
              >
                {passwordValidation.hasUpperCase &&
                passwordValidation.hasLowerCase ? (
                  <CheckSmallIcon />
                ) : (
                  <CloseSmallIcon />
                )}
                <span>{t('Use uppercase and lowercase letters')}</span>
              </div>
              <div
                className={`requirement ${passwordValidation.hasNumber ? 'valid' : 'invalid'}`}
              >
                {passwordValidation.hasNumber ? (
                  <CheckSmallIcon />
                ) : (
                  <CloseSmallIcon />
                )}
                <span>{t('Use numbers')}</span>
              </div>
              <div
                className={`requirement ${passwordValidation.hasSpecialChar ? 'valid' : 'invalid'}`}
              >
                {passwordValidation.hasSpecialChar ? (
                  <CheckSmallIcon />
                ) : (
                  <CloseSmallIcon />
                )}
                <span>{t('Use special characters')}</span>
              </div>
              <div
                className={`requirement ${passwordValidation.hasMinLength ? 'valid' : 'invalid'}`}
              >
                {passwordValidation.hasMinLength ? (
                  <CheckSmallIcon />
                ) : (
                  <CloseSmallIcon />
                )}
                <span>{t('Use password of 12 to 64 characters')}</span>
              </div>
            </div>

            <ControlledFormItem
              name="confirmPassword"
              control={control as never}
              errors={isMismatch ? errors : {}}
              label={t('Confirm Password')}
              inputType="password"
              inputProps={{
                placeholder: t('Repeat your password'),
                autoComplete: 'new-password',
              }}
            />

            <Form.Item>
              <Button
                htmlType="submit"
                variant="primary"
                size="lg"
                disabled={
                  loading ||
                  isSubmitting ||
                  success ||
                  !password ||
                  !confirmPassword ||
                  !allRulesValid ||
                  password !== confirmPassword
                }
              >
                {t('Confirm')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
